import React, { useCallback, useState, useRef, useEffect } from "react";
import { Tooltip } from "@heroui/tooltip";
import { getLocationTypeIcon } from "@/lib/general";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { PlusIcon, MinusIcon } from "lucide-react";
import logger from "@/utils/logger";

interface CustomMarkerProps {
  longitude: number;
  latitude: number;
  markerName?: string;
  type: string;
}

const CustomMarker: React.FC<CustomMarkerProps> = ({
  longitude,
  latitude,
  markerName,
  type,
}) => {
  const getMarkerContent = () => {
    switch (type) {
      default:
        return (
          <div className="bg-white hover:bg-indigo-50 rounded-full ring-1 ring-indigo-600 hover:ring-indigo-700 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <i
              className={`fa-solid ${getLocationTypeIcon(
                type
              )} text-indigo-600 text-[12px]`}
            ></i>
          </div>
        );
    }
  };

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      onClick={() => {
        logger.log("Marker clicked");
      }}
      style={{ cursor: "pointer" }}
    >
      <Tooltip
        content={
          <div className="flex flex-row items-center justify-center gap-2 p-1">
            <div className="flex items-center gap-2">
              <i
                className={`fa-solid ${getLocationTypeIcon(
                  type
                )} text-indigo-600 text-[12px]`}
              ></i>
              <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                {markerName || ""}
              </p>
            </div>
          </div>
        }
        onClick={() => {
          logger.log("Tooltip clicked");
        }}
        closeDelay={0}
        radius="md"
        showArrow={true}
        classNames={{
          content: "shadow-lg border border-neutral-100",
        }}
      >
        {getMarkerContent()}
      </Tooltip>
    </Marker>
  );
};

interface MapViewProps {
  latitude: number;
  longitude: number;
  markerName?: string;
  type: string;
}

const MapView: React.FC<MapViewProps> = ({
  latitude,
  longitude,
  markerName,
  type,
}) => {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [overViewMapViewport, setOverViewMapViewport] = useState({
    longitude: longitude,
    latitude: latitude,
    zoom: 11,
  });

  const onLoad = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleZoomIn = useCallback(() => {
    setOverViewMapViewport((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 15),
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setOverViewMapViewport((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, 11),
    }));
  }, []);

  return (
    <div className="w-full h-full rounded-md overflow-hidden relative">
      <Map
        reuseMaps
        mapboxAccessToken={mapboxToken}
        ref={(ref) => (mapRef.current = ref && ref.getMap())}
        {...overViewMapViewport}
        onLoad={onLoad}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
        attributionControl={false}
        minZoom={11}
        maxZoom={15}
      >
        <CustomMarker
          longitude={longitude}
          latitude={latitude}
          markerName={markerName}
          type={type}
        />
      </Map>

      {/* Add zoom controls */}
      <div className="absolute right-4 bottom-4 flex flex-col gap-1">
        <button
          onClick={handleZoomIn}
          className="flex items-center justify-center bg-white w-8 h-8 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          aria-label="Zoom in"
        >
          <PlusIcon className="w-4 h-4 text-neutral-600" />
        </button>
        <button
          onClick={handleZoomOut}
          className="flex items-center justify-center bg-white w-8 h-8 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          aria-label="Zoom out"
        >
          <MinusIcon className="w-4 h-4 text-neutral-600" />
        </button>
      </div>
    </div>
  );
};

export default MapView;
